import { Controller } from 'stimulus'

const STACKED   = '(max-width: 1024px)'
const MIN_WIDTH = 360
const KEY_STEP  = 32

// show.js.erb swaps the card and the panel's contents, never the panel itself.
export default class extends Controller {
  static targets = ['layout', 'list', 'card', 'viewer', 'frame', 'title', 'openLink', 'handle']

  connect() {
    this.isOpen = this.data.get('open') === 'true'
    this.width  = parseInt(this.data.get('width'), 10) || null
    this.applyWidth()

    this.onKeydown = this.onKeydown.bind(this)
    document.addEventListener('keydown', this.onKeydown)

    // Without a PDF renderer on the server the preview image fails: the sheet keeps its generic face.
    this.onMediaError = (e) => {
      const sheet = e.target.closest && e.target.closest('.pi-thumb__sheet')
      if (sheet) sheet.classList.add('is-broken')
    }
    this.element.addEventListener('error', this.onMediaError, true)

    if (window.MutationObserver) {
      this.frameObserver = new MutationObserver(() => this.sync())
      this.frameObserver.observe(this.frameTarget, { childList: true })
      this.cardObserver = new MutationObserver(() => this.sync())
      this.cardObserver.observe(this.cardTarget, { childList: true })
    }

    this.sync()
    requestAnimationFrame(() => this.element.classList.remove('pi-page--still'))
  }

  disconnect() {
    document.removeEventListener('keydown', this.onKeydown)
    this.element.removeEventListener('error', this.onMediaError, true)
    if (this.frameObserver) this.frameObserver.disconnect()
    if (this.cardObserver) this.cardObserver.disconnect()
  }

  open() {
    if (this.isOpen || !this.hasInvoice()) return

    this.isOpen = true
    this.remember({ open: true })
    this.render()
  }

  close() {
    if (!this.isOpen) return

    this.isOpen = false
    this.remember({ open: false })
    this.render()
    this.scrollSelectedIntoView()
  }

  startResize(e) {
    if (e.button !== undefined && e.button !== 0) return
    e.preventDefault()

    const handle = this.handleTarget
    if (handle.setPointerCapture && e.pointerId !== undefined) handle.setPointerCapture(e.pointerId)
    this.element.classList.add('is-resizing')

    const move = (ev) => this.setWidth(window.innerWidth - ev.clientX)
    const stop = () => {
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', stop)
      handle.removeEventListener('pointercancel', stop)
      this.element.classList.remove('is-resizing')
      this.saveWidth()
    }

    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', stop)
    handle.addEventListener('pointercancel', stop)
  }

  resizeWithKeys(e) {
    const delta = { ArrowLeft: KEY_STEP, ArrowRight: -KEY_STEP }[e.key]
    if (!delta) return

    e.preventDefault()
    this.setWidth(this.currentWidth() + delta)
    this.saveWidth()
  }

  resetWidth() {
    this.width = null
    this.applyWidth()
    this.remember({ width: 0 })
  }

  toggle() {
    this.isOpen ? this.close() : this.open()
  }

  dragOver(e) {
    e.preventDefault()
    e.currentTarget.classList.add('is-dragover')
  }

  dragLeave(e) {
    e.currentTarget.classList.remove('is-dragover')
  }

  dropFile(e) {
    e.preventDefault()
    const zone  = e.currentTarget
    const input = zone.querySelector('input[type=file]')
    zone.classList.remove('is-dragover')
    if (!input || !e.dataTransfer || !e.dataTransfer.files.length) return

    input.files = e.dataTransfer.files
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // jquery_ujs raises ajax:complete as a jQuery event, which a Stimulus action cannot hear.
  bindUpload() {
    const zone = this.cardTarget.querySelector('form.pi-drop')
    if (!zone || zone.dataset.piBound || !window.jQuery) return

    zone.dataset.piBound = '1'
    window.jQuery(zone).on('ajax:complete', () => this.uploadSettled(zone))
  }

  uploadSettled(zone) {
    if (!zone.isConnected) return

    const spinner = zone.querySelector('[data-target="document.spinner"]')
    const label   = zone.querySelector('[data-target="document.label"]')
    const input   = zone.querySelector('input[type=file]')
    if (spinner) spinner.classList.add('d-none')
    if (label) label.classList.remove('d-none')
    if (input) input.value = ''
  }

  // Private

  onKeydown(e) {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return
    if (this.isTyping(e.target) || document.querySelector('.modal.show')) return

    if (e.key === 'p' || e.key === 'P') {
      if (!this.hasInvoice()) return
      e.preventDefault()
      this.toggle()
    } else if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault()
      this.close()
    } else if (this.isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault()
      this.step(e.key === 'ArrowDown' ? 1 : -1)
    }
  }

  step(direction) {
    const links   = Array.from(this.listTarget.querySelectorAll('a[id^="small_"]'))
    const current = links.findIndex((link) => link.querySelector('.selected-card'))
    const next    = links[current + direction]
    if (!next) return

    // application-list reads the clicked element inside the card, which a dispatched click has not.
    this.listTarget.querySelectorAll('.selected-card').forEach((card) => card.classList.remove('selected-card'))
    const item = next.querySelector('.small_cards__item')
    if (item) item.classList.add('selected-card')
    next.click()
  }

  sync() {
    this.bindUpload()
    // Only a page with no invoice left closes the viewer; the spinner between two cards does not.
    const thumb = this.thumbnail()
    if (this.isOpen && this.cardTarget.querySelector('.big-card__empty') && !this.hasInvoice()) this.isOpen = false

    if (this.hasTitleTarget) this.titleTarget.textContent = (thumb && thumb.dataset.filename) || this.data.get('viewerLabel') || ''

    const frame = this.frameTarget.querySelector('iframe[src]')
    this.openLinkTargets.forEach((link) => {
      if (frame) {
        link.setAttribute('href', frame.getAttribute('src'))
        link.classList.remove('d-none')
      } else {
        link.removeAttribute('href')
        link.classList.add('d-none')
      }
    })

    this.render()
  }

  render() {
    this.element.classList.toggle('is-viewer-open', this.isOpen)
    this.layoutTarget.classList.toggle('is-viewer-open', this.isOpen)
    this.viewerTarget.setAttribute('aria-hidden', String(!this.isOpen))

    const thumb = this.thumbnail()
    if (thumb) thumb.setAttribute('aria-expanded', String(this.isOpen))
  }

  setWidth(width) {
    const room = this.layoutTarget.getBoundingClientRect().width
    const max  = Math.max(MIN_WIDTH, Math.round(room - 260 - 420 - 40))
    this.width = Math.min(max, Math.max(MIN_WIDTH, Math.round(width)))
    this.applyWidth()
  }

  applyWidth() {
    if (this.width) {
      this.element.style.setProperty('--pi-panel-width', `${this.width}px`)
    } else {
      this.element.style.removeProperty('--pi-panel-width')
    }
  }

  currentWidth() {
    return this.viewerTarget.getBoundingClientRect().width
  }

  saveWidth() {
    if (this.width) this.remember({ width: this.width })
  }

  remember(state) {
    const token = document.querySelector('meta[name="csrf-token"]')
    fetch(this.data.get('panelUrl'), {
      method:      'PATCH',
      credentials: 'same-origin',
      headers:     { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': token ? token.content : '' },
      body:        JSON.stringify(state)
    }).catch(() => {})
  }

  stacked() {
    return window.matchMedia && window.matchMedia(STACKED).matches
  }

  hasInvoice() {
    return !!this.cardTarget.querySelector('[id^="card-"]')
  }

  thumbnail() {
    return this.cardTarget.querySelector('[data-pi-thumb]')
  }

  scrollSelectedIntoView() {
    const selected = this.listTarget.querySelector('.selected-card')
    if (selected && selected.scrollIntoView && !this.stacked()) {
      selected.scrollIntoView({ block: 'nearest' })
    }
  }

  isTyping(target) {
    if (!target || !target.closest) return false
    return target.isContentEditable || !!target.closest('input, textarea, select, [contenteditable]')
  }
}
